// A FORMA DO ARQUIVO POSICIONAL DE TESTE — campos, tipos de registro e formatos.
//
// Mora fora do adaptador pelo mesmo motivo que `alece/alece-colunas.ts`: o
// CATALOGO precisa das colunas para montar o documento tecnico, e o adaptador
// precisa do catalogo para saber a versao. Com a declaracao aqui, os dois leem
// o mesmo array e nao ha ciclo de import.
//
// Este layout NAO existe para ente nenhum. Ele existe para que os valores
// `largura-fixa`, `abertura-detalhe-fechamento`, `fechamento-com-totais`,
// `centavos-implicitos`, `com-sinal`, `competencia-aamm`, `matricula-com-digito`,
// `sem-nome`, `linhas-vazias-no-fim` e `reconhecido-por-conteudo` da matriz de
// cenarios tenham um exemplo que os exercite — um valor sem fixture nao e'
// suportado, e' promessa (ver `matriz-cenarios.ts`).
import { SITUACAO_CARGA } from "../../retorno.js";
export const TESTE_POSICIONAL_VERSAO = "1.0";
/**
 * Toda linha tem 120 posicoes — inclusive a abertura e o fechamento, que
 * preenchem o resto com espaco. E' a largura que `reconhecer` usa, junto do
 * primeiro caractere, para dizer "este arquivo e' meu" sem olhar o nome.
 */
export const LARGURA_LINHA_POSICIONAL = 120;
/** Codigo do registro na posicao 1: `0` abre, `1` e `2` sao detalhe, `9` fecha. */
export const TIPO_REGISTRO_POSICIONAL = {
    posicao: 1,
    largura: 1,
    abertura: "0",
    detalhe: ["1", "2"],
    fechamento: "9",
};
export const REGISTRO_SERVIDOR = "1";
export const REGISTRO_DESCONTO = "2";
/**
 * Numero em CENTAVOS INTEIROS, sem separador: `0000000351731` e' 3.517,31.
 *
 * `zerosAEsquerda` porque o arquivo enche o campo com zero, e o leitor de
 * largura fixa devolve o texto ja sem eles.
 */
export const CENTAVOS = { decimal: "implicito-2", sinal: "nenhum", zerosAEsquerda: true };
/**
 * Centavos com o sinal na ULTIMA posicao do campo (`00000023317+`).
 *
 * Sinal de sufixo e' coisa de largura fixa: ali ele e' uma coluna, e por isso
 * a escrita SEMPRE emite `+` ou `-` — um campo que as vezes tem sinal e as
 * vezes nao desalinharia todo o resto da linha.
 */
export const CENTAVOS_COM_SINAL = {
    decimal: "implicito-2",
    sinal: "sufixo",
    zerosAEsquerda: true,
};
/** Quantidade inteira do fechamento, com zeros a esquerda. */
export const QUANTIDADE = {
    decimal: "ponto",
    casas: 0,
    sinal: "nenhum",
    zerosAEsquerda: true,
};
// ---------------------------------------------------------------- registros
//
// `preenchimento: "0"` com `alinhamento: "direita"` no campo NUMERICO: o leitor
// tira os zeros e entrega o numero. Nos campos de identidade (matricula, CPF,
// agencia, conta) o preenchimento fica em ESPACO de proposito — zero a esquerda
// de matricula e' conteudo, e tira-lo faria `0001300` virar `1300`.
export const CAMPOS_ABERTURA = [
    { nome: "tipo", inicio: 1, largura: 1 },
    { nome: "competencia", inicio: 2, largura: 4 },
    { nome: "codigoFolha", inicio: 6, largura: 6 },
];
export const CAMPOS_SERVIDOR = [
    { nome: "tipo", inicio: 1, largura: 1 },
    { nome: "matricula", inicio: 2, largura: 7 },
    { nome: "cpf", inicio: 9, largura: 11 },
    { nome: "situacao", inicio: 20, largura: 2 },
    { nome: "admissao", inicio: 22, largura: 8 },
    { nome: "cargo", inicio: 30, largura: 30 },
    { nome: "vinculo", inicio: 60, largura: 15 },
    { nome: "salario", inicio: 75, largura: 13, alinhamento: "direita", preenchimento: "0" },
    { nome: "banco", inicio: 88, largura: 3 },
    { nome: "agencia", inicio: 91, largura: 6 },
    { nome: "conta", inicio: 97, largura: 12 },
];
export const CAMPOS_DESCONTO = [
    { nome: "tipo", inicio: 1, largura: 1 },
    { nome: "matricula", inicio: 2, largura: 7 },
    { nome: "verba", inicio: 9, largura: 6 },
    { nome: "descricao", inicio: 15, largura: 30 },
    { nome: "contrato", inicio: 45, largura: 20 },
    // Sem preenchimento declarado: o campo termina no SINAL, e mandar o leitor
    // comer zeros a esquerda deixaria o valor zero reduzido a "+", que nao e'
    // numero nenhum.
    { nome: "valor", inicio: 65, largura: 14 },
    { nome: "parcela", inicio: 79, largura: 3, alinhamento: "direita", preenchimento: "0" },
    { nome: "parcelasTotal", inicio: 82, largura: 3, alinhamento: "direita", preenchimento: "0" },
];
export const CAMPOS_FECHAMENTO = [
    { nome: "tipo", inicio: 1, largura: 1 },
    { nome: "quantidade", inicio: 2, largura: 6, alinhamento: "direita", preenchimento: "0" },
    { nome: "soma", inicio: 8, largura: 14 },
];
// ---------------------------------------------------------------- documento
//
// O documento tecnico sai desta lista. Como um arquivo posicional nao tem
// "coluna", o numero e' a ORDEM em que os campos aparecem no papel, e o nome
// carrega o registro a que pertence — e' assim que o integrador le "o campo
// `servidor.salario` fica nas posicoes 75 a 87".
const posicoes = (inicio, largura) => `Posições ${inicio} a ${inicio + largura - 1}.`;
export const COLUNAS_TESTE_POSICIONAL = [
    {
        nome: "registro.tipo",
        coluna: 1,
        kind: "codigo",
        obrigatorio: true,
        codigos: ["0", "1", "2", "9"],
        largura: 1,
        descricao: `${posicoes(1, 1)} \`0\` abertura, \`1\` servidor, \`2\` desconto, \`9\` fechamento.`,
    },
    {
        nome: "abertura.competencia",
        coluna: 2,
        kind: "competencia",
        obrigatorio: true,
        largura: 4,
        descricao: `${posicoes(2, 4)} Competência da folha em **AAMM**, sem o século.`,
    },
    {
        nome: "abertura.codigoFolha",
        coluna: 3,
        kind: "alfa",
        largura: 6,
        descricao: `${posicoes(6, 6)} Código da folha no sistema do ente. Informativo.`,
    },
    {
        nome: "servidor.matricula",
        coluna: 4,
        kind: "alfa",
        obrigatorio: true,
        largura: 7,
        descricao: `${posicoes(2, 7)} Matrícula com **dígito verificador** na última posição.`,
    },
    {
        nome: "servidor.cpf",
        coluna: 5,
        kind: "cpf",
        obrigatorio: true,
        largura: 11,
        descricao: `${posicoes(9, 11)} Onze dígitos, sem máscara.`,
    },
    {
        nome: "servidor.situacao",
        coluna: 6,
        kind: "codigo",
        obrigatorio: true,
        codigos: SITUACAO_CARGA,
        largura: 2,
        descricao: `${posicoes(20, 2)} Situação funcional, já no código do padrão Atlas.`,
    },
    {
        nome: "servidor.admissao",
        coluna: 7,
        kind: "dataBr",
        largura: 8,
        descricao: `${posicoes(22, 8)} **DDMMAAAA**, sem separador.`,
    },
    {
        nome: "servidor.cargo",
        coluna: 8,
        kind: "alfa",
        largura: 30,
        descricao: `${posicoes(30, 30)} Preenchido à direita com espaço.`,
    },
    {
        nome: "servidor.vinculo",
        coluna: 9,
        kind: "alfa",
        largura: 15,
        descricao: `${posicoes(60, 15)} Preenchido à direita com espaço.`,
    },
    {
        nome: "servidor.salario",
        coluna: 10,
        kind: "dinheiro",
        obrigatorio: true,
        largura: 13,
        preenche: "zeros",
        descricao: `${posicoes(75, 13)} **Centavos inteiros**, sem separador decimal e sem sinal.`,
    },
    {
        nome: "servidor.banco",
        coluna: 11,
        kind: "alfa",
        largura: 3,
        descricao: `${posicoes(88, 3)} Código do banco de crédito, três dígitos.`,
    },
    {
        nome: "servidor.agencia",
        coluna: 12,
        kind: "alfa",
        largura: 6,
        descricao: `${posicoes(91, 6)} Zeros à esquerda são conteúdo.`,
    },
    {
        nome: "servidor.conta",
        coluna: 13,
        kind: "alfa",
        largura: 12,
        descricao: `${posicoes(97, 12)} Zeros à esquerda são conteúdo.`,
    },
    {
        nome: "desconto.matricula",
        coluna: 14,
        kind: "alfa",
        obrigatorio: true,
        largura: 7,
        descricao: `${posicoes(2, 7)} Casa com \`servidor.matricula\`, dígito incluído.`,
    },
    {
        nome: "desconto.verba",
        coluna: 15,
        kind: "alfa",
        obrigatorio: true,
        largura: 6,
        descricao: `${posicoes(9, 6)} Código da rubrica de desconto na folha.`,
    },
    {
        nome: "desconto.descricao",
        coluna: 16,
        kind: "alfa",
        largura: 30,
        descricao: `${posicoes(15, 30)} Descrição da verba como sai no holerite.`,
    },
    {
        nome: "desconto.contrato",
        coluna: 17,
        kind: "alfa",
        largura: 20,
        descricao: `${posicoes(45, 20)} **Número do contrato (ADF)** devolvido pelo ente.`,
    },
    {
        nome: "desconto.valor",
        coluna: 18,
        kind: "dinheiro",
        obrigatorio: true,
        largura: 14,
        descricao: `${posicoes(65, 14)} **Centavos inteiros** com o sinal (\`+\`/\`-\`) na última posição.`,
    },
    {
        nome: "desconto.parcela",
        coluna: 19,
        kind: "num",
        largura: 3,
        preenche: "zeros",
        descricao: `${posicoes(79, 3)} Parcela descontada nesta competência.`,
    },
    {
        nome: "desconto.parcelasTotal",
        coluna: 20,
        kind: "num",
        largura: 3,
        preenche: "zeros",
        descricao: `${posicoes(82, 3)} Total de parcelas do contrato.`,
    },
    {
        nome: "fechamento.quantidade",
        coluna: 21,
        kind: "num",
        obrigatorio: true,
        largura: 6,
        preenche: "zeros",
        descricao: `${posicoes(2, 6)} Quantidade de linhas de detalhe (\`1\` + \`2\`). **Conferida** contra o conteúdo.`,
    },
    {
        nome: "fechamento.soma",
        coluna: 22,
        kind: "dinheiro",
        obrigatorio: true,
        largura: 14,
        descricao: `${posicoes(8, 14)} Soma dos valores dos descontos, em centavos com sinal. **Conferida** contra o conteúdo.`,
    },
];
//# sourceMappingURL=posicional-colunas.js.map